import { Alert, Button, Form, Input, Modal, Select } from "antd";
import { message } from "utils/ciq-message";
import FormItem from "antd/es/form/FormItem";
import {
  ErrorMessages,
  EUserTypes,
  Regex,
  SidepanelStyles
} from "constants/index";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PROJECT_COMPANY_LIST_FOR_INVITE,
  PROJECT_USERS_BY_PROJECT,
  QUERY_PROJECT_ROLES
} from "admin-app/services/ciq-queries";
import { MUTATION_ADMIN_INSERT_PROJECT_USER } from "admin-app/services/mutations";
import { getGQL, toProjectRoleSelectOptions } from "utils/utils";
import {
  getAdminInsertUserResultMessage,
  hasAdminInsertUserFailures,
  toAdminInsertUserDisplayMessage
} from "admin-app/utils/admin-insert-user-result";
import {
  buildUserInviteMessage,
  copyTextToClipboard
} from "admin-app/utils/user-invite-message";

export type Role = {
  id: number;
  name: string;
};

export type InviteProjectUserProps = {
  modelTitle: string;
  setDrawerOpen: (open: boolean) => void;
  showDrawerOpen: boolean;
  existingUsers: Array<any>;
  projectId: string;
  subscriptionId: string;
  defaultVendorId?: string;
  onSuccess?: () => void;
};

function InviteProjectUser({
  modelTitle,
  setDrawerOpen,
  showDrawerOpen,
  existingUsers,
  projectId,
  subscriptionId,
  defaultVendorId,
  onSuccess
}: InviteProjectUserProps) {
  const [form] = Form.useForm();
  const [responseLoading, setResponseLoading] = useState(false);
  const [projectVendors, setProjectVendors] = useState<Array<any>>([]);
  const [userTypeId, setUserTypeId] = useState<number | null>(null);
  const [createdInviteMessage, setCreatedInviteMessage] = useState<
    string | null
  >(null);
  const resolveCompanyUserTypeId = useCallback(
    (companyId: string | undefined) => {
      if (!companyId) return null;
      const companyDetails = projectVendors.find((v) => v.id === companyId);
      const projectVendorsForCompany = companyDetails?.project_vendors;
      return projectVendorsForCompany?.length
        ? projectVendorsForCompany[0]?.type_id
        : null;
    },
    [projectVendors]
  );

  useEffect(() => {
    if (!showDrawerOpen) {
      setCreatedInviteMessage(null);
      setUserTypeId(null);
    }
  }, [showDrawerOpen]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setCreatedInviteMessage(null);
  };

  const handleCopyInviteMessage = async () => {
    // The only caller is the "Copy Invite Message" button, which is rendered solely when createdInviteMessage is truthy, so this guard can never fire.
    /* v8 ignore next */
    if (!createdInviteMessage) return;

    try {
      await copyTextToClipboard(createdInviteMessage);
      message.success("Invite message copied to clipboard.");
    } catch {
      message.error("Unable to copy invite message.");
    }
  };

  const { data: companyData, loading: companyDataLoading } = useCIQQuery(
    PROJECT_COMPANY_LIST_FOR_INVITE,
    {
      variables: { project_id: projectId },
      skip: !projectId || !showDrawerOpen
    }
  );

  const { data: projectSubscriptionRoles, loading: projectRolesLoading } =
    useCIQQuery(QUERY_PROJECT_ROLES, {
      variables: {
        where: { type_id: { _eq: EUserTypes.GENERAL_CONTRACTOR } }
      },
      skip: !showDrawerOpen
    });

  const projectRoleOptions = useMemo(
    () => toProjectRoleSelectOptions(projectSubscriptionRoles?.project_role),
    [projectSubscriptionRoles?.project_role]
  );

  const { query: projectUsersQuery } = getGQL(PROJECT_USERS_BY_PROJECT);

  const [adminInsertProjectUser] = useCIQMutation(
    MUTATION_ADMIN_INSERT_PROJECT_USER,
    {
      refetchQueries: [
        {
          query: projectUsersQuery,
          variables: { where: { project_id: { _eq: projectId } } }
        }
      ]
    }
  );

  useEffect(() => {
    if (!companyData?.subscription_vendors) return;

    try {
      const vendors = [...companyData.subscription_vendors].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setProjectVendors(vendors);

      if (!defaultVendorId) return;

      const defaultCompany = vendors.find((v) => v.id === defaultVendorId);
      const defaultProjectVendors = defaultCompany?.project_vendors;
      setUserTypeId(
        defaultProjectVendors?.length ? defaultProjectVendors[0]?.type_id : null
      );
      form.setFieldsValue({
        company: defaultVendorId,
        project_role_id: null
      });
    } catch {
      //
    }
  }, [companyData, defaultVendorId, form]);

  const onSendInvite = async (values: any) => {
    const resolvedUserTypeId =
      userTypeId ?? resolveCompanyUserTypeId(values.company);

    if (resolvedUserTypeId == null) {
      message.error("Unable to determine company role for this project.");
      return;
    }

    setResponseLoading(true);
    try {
      const response = await adminInsertProjectUser({
        variables: {
          company_id: values.company,
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          subscription_id: subscriptionId,
          project_id: projectId,
          project_permission_id: values.project_role_id,
          user_type_id: resolvedUserTypeId,
          phone: values.phone ?? ""
        }
      });

      if (!response.success) {
        message.error(
          toAdminInsertUserDisplayMessage(
            response.error?.message ?? response.errorMsg,
            "Unable to invite user to project."
          )
        );
        return;
      }

      const result = response.data?.admin_insert_subscription_user;

      if (hasAdminInsertUserFailures(result?.failures)) {
        message.error(
          getAdminInsertUserResultMessage(
            result,
            "Unable to invite user to project."
          )
        );
        return;
      }

      const successMessage = getAdminInsertUserResultMessage(result, "");
      if (successMessage) {
        message.success(successMessage);
        setCreatedInviteMessage(
          buildUserInviteMessage("project", {
            firstName: values.first_name,
            email: values.email,
            password: values.password
          })
        );
        onSuccess?.();
      } else {
        message.error("Unable to invite user to project.");
      }
    } catch {
      message.error("An error occurred while inviting user to project.");
    } finally {
      setResponseLoading(false);
    }
  };

  return (
    <Modal
      className="custom-drawer"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div className="flex justify-end gap-2">
          {createdInviteMessage && (
            <Button
              onClick={handleCopyInviteMessage}
              data-testid="copy-invite-message-btn"
            >
              Copy Draft Email
            </Button>
          )}
          {createdInviteMessage ? (
            <Button
              type="primary"
              onClick={closeDrawer}
              data-testid="close-invite-user-btn"
            >
              Close
            </Button>
          ) : (
            <Button
              form="admin-invite-project-user-form"
              htmlType="submit"
              disabled={responseLoading}
              loading={responseLoading}
              type="primary"
              data-testid="send-invite-button"
            >
              Create User
            </Button>
          )}
        </div>
      }
      open={showDrawerOpen}
      onCancel={closeDrawer}
      destroyOnHidden
    >
      <div className="flex-col h-full overflow-y overflow-x-hidden px-3">
        {createdInviteMessage && (
          <Alert
            type="success"
            showIcon
            className="mb-3"
            data-testid="invite-user-success-alert"
            // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title); expose a class-independent hook for UI automation
            title={
              <span data-testid="invite-user-success-alert-message">
                User created. Copy the draft email to send to the user.
              </span>
            }
          />
        )}
        <Form
          id="admin-invite-project-user-form"
          preserve={false}
          onFinish={onSendInvite}
          form={form}
          scrollToFirstError
          layout="vertical"
          disabled={Boolean(createdInviteMessage)}
          data-testid="invite-project-user-form"
        >
          <FormItem name="company" label="Company">
            <Select
              data-testid="company-select"
              loading={companyDataLoading}
              disabled={companyDataLoading || Boolean(createdInviteMessage)}
              onChange={(value) => {
                setUserTypeId(resolveCompanyUserTypeId(value));
                form.setFieldsValue({ project_role_id: null });
              }}
              showSearch
              filterOption={(input, option) => {
                const inputMes = input.toString().toLowerCase();
                const label = (option?.label || "").toLowerCase();
                return label.includes(inputMes);
              }}
              options={projectVendors?.map((c) => ({
                value: c.id,
                label: c.name
              }))}
            />
          </FormItem>

          <FormItem
            name="first_name"
            label="First Name"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.FirstName
              }
            ]}
          >
            <Input data-testid="input-firstName" />
          </FormItem>
          <FormItem
            name="last_name"
            label="Last Name"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.LastName
              }
            ]}
          >
            <Input data-testid="input-lastName" />
          </FormItem>
          <FormItem
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.EmailRequired
              },
              {
                type: "email",
                validateTrigger: "onSubmit",
                message: ErrorMessages.EmailInvalid
              },
              {
                validateTrigger: "onSubmit",
                validator(_, value: string) {
                  if (value === undefined || !value) return Promise.resolve();
                  const email = value.toLowerCase();
                  if (existingUsers.some((e) => e.user?.email === email)) {
                    return Promise.reject(new Error());
                  }
                  return Promise.resolve();
                },
                message: ErrorMessages.EmailProjectUserExist
              }
            ]}
          >
            <Input data-testid="input-email" />
          </FormItem>
          <FormItem
            name="phone"
            label="Phone Number"
            rules={[
              {
                message: ErrorMessages.PhoneNumber,
                validateTrigger: "onSubmit",
                pattern: Regex.phoneNumber
              }
            ]}
          >
            <Input data-testid="input-phone" />
          </FormItem>
          <FormItem
            name="password"
            label="Password"
            rules={[
              {
                validateTrigger: "onChange",
                required: true,
                pattern: Regex.password,
                message: ErrorMessages.PasswordRequired
              }
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Enter Password" />
          </FormItem>
          <FormItem
            name="confirm_password"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                validateTrigger: "onChange",
                message: ErrorMessages.ConfirmPasswordRequired
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(ErrorMessages.ConfirmPasswordRequired)
                  );
                }
              })
            ]}
          >
            <Input.Password placeholder="Confirm Password" />
          </FormItem>
          <FormItem
            name="project_role_id"
            label="Project Role"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.ProjectRole,
                validator(_, value: string) {
                  if (!value) {
                    return Promise.reject(new Error());
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select
              loading={projectRolesLoading}
              options={projectRoleOptions}
              data-testid="project-role-select"
            />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
}

export default InviteProjectUser;
